// DLC_09 · Kolejka wysyłki e-Doręczeń.
//
// Trzy kroki, trzy różne osoby: autor dodaje do kolejki, dyrektor techniczny albo CEO
// akceptuje bądź odrzuca, a wysyła wyłącznie CEO. Ekran pokazuje każdemu tylko te przyciski,
// które faktycznie może kliknąć — bramki są i tak po stronie API.
import { useMemo, useState } from "react";
import { Ban, Check, Paperclip, Plus, RefreshCw, Send, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  AttachmentLinks,
  EmptyState,
  HubNav,
  PageHeader,
  StatTile,
  TableShell,
  Th,
} from "@/features/power_projects/components/ui";
import { fmtDateTime } from "@/features/power_projects/utils";
import { DLC09_HUB_LINKS, StatusChip, formatBytes } from "../components/ui";
import {
  useEdoreczeniaSummary,
  useMailboxOptions,
  useOutbox,
  useOutboxMutations,
  useRecipients,
} from "../hooks";
import type { OutgoingMessage } from "../types";

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <label className="block">
      <span className="section-label">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">{hint}</p>}
    </label>
  );
}

function QueueForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: mailboxes } = useMailboxOptions();
  const [query, setQuery] = useState("");
  const { data: recipients } = useRecipients(query);
  const { queue } = useOutboxMutations();

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientAde, setRecipientAde] = useState("");
  const [mailboxId, setMailboxId] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState("");

  // Jedna skrzynka w firmie = nie ma czego wybierać; podstawiamy ją z automatu.
  const onlyMailbox = mailboxes?.length === 1 ? mailboxes[0].id : "";
  const effectiveMailbox = mailboxId || onlyMailbox;

  const submit = () => {
    setError("");
    const fd = new FormData();
    fd.append("mailbox", effectiveMailbox);
    fd.append("subject", subject);
    fd.append("body", body);
    fd.append("recipient_name", recipientName);
    fd.append("recipient_ade", recipientAde);
    files.forEach((f) => fd.append("files", f));
    queue.mutate(fd, {
      onSuccess: () => {
        setSubject("");
        setBody("");
        setRecipientName("");
        setRecipientAde("");
        setFiles([]);
        onClose();
      },
      onError: (e: unknown) => {
        const detail = (e as { response?: { data?: { errors?: { detail?: string } } } })?.response
          ?.data?.errors?.detail;
        setError(detail || "Nie udało się dodać do kolejki.");
      },
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Nowa przesyłka e-Doręczeń" size="lg">
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Skrzynka nadawcza">
            <select
              className="input-base w-full"
              value={effectiveMailbox}
              onChange={(e) => setMailboxId(e.target.value)}
            >
              <option value="">— wybierz —</option>
              {(mailboxes ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} ({m.environment_display})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Znajdź urząd" hint="Podpowiadają się tylko urzędy z adresem e-Doręczeń.">
            <input
              className="input-base w-full"
              placeholder="np. Kowalewo Pomorskie"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {!!query && (
              <div className="mt-1 max-h-36 overflow-y-auto border border-[var(--color-border)]">
                {recipients?.length ? (
                  recipients.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="block w-full px-2 py-1 text-left text-xs hover:bg-[var(--color-bg-elevated)]"
                      onClick={() => {
                        setRecipientName(r.name);
                        setRecipientAde(r.edelivery_address);
                        setQuery("");
                      }}
                    >
                      {r.name}{" "}
                      <span className="text-[var(--color-text-muted)]">{r.city}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-2 py-1 text-[11px] text-[var(--color-text-muted)]">
                    Brak urzędu z adresem e-Doręczeń — uzupełnij adres w rejestrze urzędów
                    albo wyślij papierowo.
                  </div>
                )}
              </div>
            )}
          </Field>

          <Field label="Odbiorca">
            <input
              className="input-base w-full"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
            />
          </Field>

          <Field label="Adres e-Doręczeń odbiorcy">
            <input
              className="input-base w-full font-mono text-xs"
              value={recipientAde}
              onChange={(e) => setRecipientAde(e.target.value)}
              placeholder="AE:PL-…"
            />
          </Field>
        </div>

        <Field label="Temat">
          <input className="input-base w-full" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>

        <Field label="Treść">
          <textarea
            className="input-base w-full"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>

        <Field label="Załączniki">
          <FileDropZone files={files} onFilesChange={setFiles} multiple />
        </Field>

        {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}

        <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-3">
          <p className="text-[11px] text-[var(--color-text-muted)]">
            Przesyłka trafi do kolejki. Nic nie wychodzi bez akceptacji i kliknięcia „Wyślij”
            przez CEO.
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              onClick={submit}
              loading={queue.isPending}
              disabled={!effectiveMailbox || !subject || !recipientAde}
            >
              Dodaj do kolejki
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function RowActions({ msg }: { msg: OutgoingMessage }) {
  const { data: summary } = useEdoreczeniaSummary();
  const { approve, reject, cancel, send, refresh } = useOutboxMutations();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const canApprove = !!summary?.can_approve && ["queued", "rejected"].includes(msg.status);
  const canReject = !!summary?.can_approve && ["queued", "approved"].includes(msg.status);
  const canSend = !!summary?.can_send && msg.status === "approved";
  const canCancel = ["queued", "approved", "rejected", "failed"].includes(msg.status);

  if (rejecting) {
    return (
      <div className="flex items-center justify-end gap-1">
        <input
          className="input-base w-44 text-xs"
          placeholder="Powód odrzucenia"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
        />
        <Button
          size="sm"
          disabled={!reason.trim() || reject.isPending}
          onClick={() => reject.mutate({ id: msg.id, reason }, { onSuccess: () => setRejecting(false) })}
        >
          Odrzuć
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>
          <X size={14} />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {canApprove && (
        <Button size="sm" variant="secondary" onClick={() => approve.mutate(msg.id)} title="Gotowa do wysłania">
          <Check size={13} className="mr-1" /> Akceptuj
        </Button>
      )}
      {canReject && (
        <Button size="sm" variant="ghost" title="Odrzuć do poprawy" onClick={() => setRejecting(true)}>
          <X size={14} />
        </Button>
      )}
      {canSend && (
        <Button size="sm" onClick={() => send.mutate(msg.id)} loading={send.isPending} title="Wyślij do operatora">
          <Send size={13} className="mr-1" /> Wyślij
        </Button>
      )}
      {["sent", "sending"].includes(msg.status) && (
        <Button size="sm" variant="ghost" title="Sprawdź dowody" onClick={() => refresh.mutate(msg.id)}>
          <RefreshCw size={14} />
        </Button>
      )}
      {canCancel && (
        <Button size="sm" variant="ghost" title="Anuluj" onClick={() => cancel.mutate(msg.id)}>
          <Ban size={14} />
        </Button>
      )}
    </div>
  );
}

export function OutboxPage() {
  const [formOpen, setFormOpen] = useState(false);
  const { data: summary } = useEdoreczeniaSummary();
  const { data: messages, isLoading } = useOutbox();

  const badges = useMemo(
    () => ({
      "/edoreczenia": summary?.queued ?? 0,
      "/edoreczenia/skrzynka": summary?.inbox_new ?? 0,
    }),
    [summary],
  );

  const delivered = (messages ?? []).filter((m) => m.status === "delivered").length;

  return (
    <div>
      <PageHeader
        title="e-Doręczenia"
        subtitle="Kolejka wysyłki: dodaj → akceptacja → wysyłka przez CEO"
        actions={
          <Button onClick={() => setFormOpen(true)}>
            <Plus size={14} className="mr-1" /> Nowa przesyłka
          </Button>
        }
      />
      <HubNav links={DLC09_HUB_LINKS} badges={badges} />

      {!!summary?.mailbox_warnings?.length && (
        <div className="mb-4 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {summary.mailbox_warnings.map((w) => (
            <div key={`${w.mailbox}-${w.kind}`}>{w.message}</div>
          ))}
        </div>
      )}

      <div className="mb-5 flex flex-wrap gap-2">
        <StatTile label="W kolejce" value={summary?.queued ?? 0} tone="warn" hint="czeka na akceptację" />
        <StatTile label="Do wysłania" value={summary?.approved ?? 0} tone="blue" hint="klika CEO" />
        <StatTile label="Odrzucone" value={summary?.rejected ?? 0} hint="do poprawy" />
        <StatTile label="Błędy wysyłki" value={summary?.failed ?? 0} tone="danger" hint="do ponowienia" />
        <StatTile label="Doręczone" value={delivered} tone="ok" hint="z dowodem odbioru" />
      </div>

      <QueueForm open={formOpen} onClose={() => setFormOpen(false)} />

      {isLoading ? (
        <Spinner />
      ) : !messages?.length ? (
        <EmptyState>
          Kolejka jest pusta — dodaj pierwszą przesyłkę przyciskiem „Nowa przesyłka”.
        </EmptyState>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Status</Th>
              <Th>Odbiorca</Th>
              <Th>Temat</Th>
              <Th>Załączniki</Th>
              <Th>Obieg</Th>
              <Th>Dowody</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {messages.map((msg) => (
              <tr key={msg.id} className="border-t border-[var(--color-border)] align-top">
                <td className="px-2 py-2">
                  <StatusChip label={msg.status_display} status={msg.status} kind="outgoing" />
                  {msg.status === "rejected" && msg.rejection_reason && (
                    <div className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                      {msg.rejection_reason}
                    </div>
                  )}
                  {msg.status === "failed" && msg.last_error && (
                    <div className="mt-1 text-[11px] text-[var(--color-primary)]">{msg.last_error}</div>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div>{msg.recipient_name}</div>
                  <div className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    {msg.recipient_ade}
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div>{msg.subject}</div>
                  {msg.source_label && (
                    <div className="text-[11px] text-[var(--color-text-muted)]">{msg.source_label}</div>
                  )}
                </td>
                <td className="px-2 py-2 text-[11px]">
                  {msg.files.length ? (
                    <span className="inline-flex items-center gap-1">
                      <Paperclip size={12} />
                      {msg.files.length}
                      <span className="text-[var(--color-text-muted)]">
                        ({formatBytes(msg.files.reduce((a, f) => a + (f.size_bytes ?? 0), 0))})
                      </span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-2 text-[11px] leading-tight">
                  <div>dodał: {msg.created_by_name ?? "—"}</div>
                  {msg.approved_by_name && <div>akceptacja: {msg.approved_by_name}</div>}
                  {msg.sent_at && (
                    <div>
                      wysłał: {msg.sent_by_name} · {fmtDateTime(msg.sent_at)}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2">
                  <AttachmentLinks
                    files={msg.evidences.filter((e) => e.file).map((e) => e.file!)}
                    itemClassName="max-w-[160px] truncate"
                  />
                </td>
                <td className="px-2 py-2 text-right">
                  <RowActions msg={msg} />
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
    </div>
  );
}
